<script setup>
const props = defineProps({
  company: {
    type: Object,
    required: true,
  },
  companyReferenceProfile: {
    type: Object,
    required: false,
    default: null,
  },
  professional: {
    type: Object,
    required: true,
  },
  matchings: {
    type: Array,
    required: true,
  },
})

const user = useStrapiUser()
const { $viewport } = useNuxtApp()
const { mapProfile } = useAssessmentMapper()
const { extractMappedDataFromCandidate } = useAssessmentHelper()

const expandedSubSection = ref('personality')

const isProfessional = computed(() => user.value?.type === 'professional')
const professionalName = computed(() =>
  props.professional.user?.contact?.first_name
    ? `${props.professional.user?.contact?.first_name} ${props.professional.user?.contact?.last_name}`
    : `Professional #${props.professional.id}`,
)

const professionalImage = computed(
  () => props.professional.user?.contact?.image,
)

const mappedProfile = computed(() =>
  mapProfile(props.companyReferenceProfile || props.company.profile),
)
const mappedProfessional = computed(() =>
  extractMappedDataFromCandidate(props.professional.candidate),
)

const subsections = computed(() => [
  {
    type: 'personality',
    title: 'Rollen',
    icon: '/images/illustration-personality.svg',
    companyItems: mappedProfile.value.topOperationalRoles?.map(
      (item) => item.label,
    ),
    professionalItems: mappedProfessional.value.topOperationalRoles?.map(
      (item) => item.label,
    ),
  },
  {
    type: 'culture',
    title: 'Cultuurwaarden',
    icon: '/images/illustration-culture.svg',
    companyItems: mappedProfile.value.topCultures?.map((item) => item.label),
    professionalItems: mappedProfessional.value.topCultures?.map(
      (item) => item.label,
    ),
  },
  {
    type: 'motivations',
    title: 'Drijfveren',
    icon: '/images/illustration-motivation.svg',
    companyItems: mappedProfile.value.topMotivations?.map((item) => item.label),
    professionalItems: mappedProfessional.value.topMotivations?.map(
      (item) => item.label,
    ),
  },
  {
    type: 'cognitive',
    title: 'Cognitief niveau',
    icon: '/images/illustration-cognitive.svg',
    companyItems: [mappedProfile.value.cognitiveLevels?.[0]?.label],
    professionalItems: [mappedProfessional.value.cognitiveLevels?.[0]?.label],
  },
])

function getMatchingScore(type) {
  const matching = props.matchings?.find((matching) => matching.type === type)
  return matching ? Number(matching?.score?.toFixed()) : null
}
</script>

<template>
  <div class="rounded bg-white p-10 shadow-sm">
    <div
      class="grid grid-cols-[minmax(0,_1fr)_44px_minmax(0,_1fr)] gap-3 lg:grid-cols-[minmax(0,_1fr)_72px_minmax(0,_1fr)] lg:gap-6"
    >
      <h2 class="font-heading text-4xl font-black uppercase text-secondary">
        Matchscore
      </h2>
      <div class=""></div>
      <div class="">
        <slot />
      </div>
    </div>

    <div
      class="mt-6 grid grid-cols-[minmax(0,_1fr)_44px_minmax(0,_1fr)] gap-3 lg:grid-cols-[minmax(0,_1fr)_72px_minmax(0,_1fr)] lg:gap-6"
    >
      <div
        class="flex min-w-0 flex-col justify-start gap-4 overflow-hidden rounded-lg bg-primary p-4 lg:flex-row-reverse lg:items-end lg:items-center"
      >
        <figure
          class="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white lg:h-10 lg:w-10"
        >
          <BaseVisual
            v-if="professionalImage"
            :file="professionalImage"
            size="thumbnail"
            class="h-6 w-6 lg:h-10 lg:w-10"
          />
          <img
            src="/images/avatar.svg"
            alt="avatar"
            class="h-6 w-6 lg:h-10 lg:w-10"
          />
        </figure>
        <h3
          class="font-sans text-sm font-semibold text-secondary lg:truncate lg:text-lg"
        >
          {{ isProfessional ? 'Jouw profiel' : professionalName }}
        </h3>
      </div>

      <div class="">
        <ClientOnly>
          <component
            :is="resolveComponent('VeProgress')"
            class="h-11 w-11 shrink-0 lg:h-[72px] lg:w-[72px]"
            :progress="getMatchingScore('total')"
            :legend-formatter="({ currentValue }) => `${currentValue}%`"
            color="#9EFF28"
            empty-color="#F4F7FA"
            color-fill="#4A154B"
            :size="$viewport.isLessThan('desktop') ? 44 : 72"
            :thickness="3"
            :empty-thickness="$viewport.isLessThan('desktop') ? 3 : 7"
            :font-size="$viewport.isLessThan('desktop') ? '12px' : '18px'"
            font-color="#FFF"
            dot="0"
          />
        </ClientOnly>
      </div>

      <div
        class="flex min-w-0 flex-col gap-4 overflow-hidden rounded-lg bg-secondary p-4 lg:flex-row lg:items-center"
      >
        <figure
          class="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-white lg:h-10 lg:w-10"
        >
          <BaseVisual
            :file="company.logo"
            size="thumbnail"
            class="h-6 w-6 lg:h-10 lg:w-10"
          />
        </figure>
        <h3
          class="font-sans text-sm font-semibold text-white lg:truncate lg:text-lg"
        >
          {{ company.name }}
        </h3>
      </div>
    </div>

    <div class="mt-4 flex flex-col gap-2">
      <section
        v-for="section in subsections"
        :key="section.type"
        class="grid grid-cols-[minmax(0,_1fr)_60px_minmax(0,_1fr)] items-start rounded-lg p-4 transition-all lg:grid-cols-[minmax(0,_1fr)_100px_minmax(0,_1fr)]"
        :class="{
          'cursor-pointer bg-gray-50 hover:bg-gray-200':
            section.type !== expandedSubSection,
          'border border-gray-50 bg-white': section.type === expandedSubSection,
        }"
        @click="expandedSubSection = section.type"
      >
        <div class="z-10 flex items-start justify-end gap-4">
          <h4
            class="mt-1.5 hidden font-sans text-sm md:block lg:text-right lg:text-lg"
          >
            {{
              isProfessional
                ? `Jouw ${section.title.toLowerCase()}`
                : section.title
            }}
          </h4>
          <img class="h-10 w-10" :src="section.icon" />
        </div>

        <div class="relative flex justify-center">
          <figure
            class="absolute h-10 w-[100px] transition-all lg:w-[140px]"
            :class="{
              ' bg-white': section.type !== expandedSubSection,
              'bg-gray-50': section.type === expandedSubSection,
            }"
          ></figure>
          <figure
            class="z-10 flex h-10 w-12 items-center justify-center rounded-[20px] bg-secondary text-xs font-semibold text-white"
          >
            <span>{{ getMatchingScore(section.type) || '-' }} %</span>
          </figure>
        </div>

        <div class="z-10 flex items-start gap-4">
          <img class="h-10 w-10" :src="section.icon" />
          <h4 class="mt-1.5 hidden font-sans text-sm md:block lg:text-lg">
            {{ section.title }}
          </h4>
        </div>

        <h4 class="mt-3 break-words text-right font-sans text-sm md:hidden">
          {{
            isProfessional
              ? `Jouw ${section.title.toLowerCase()}`
              : section.title
          }}
        </h4>

        <div class="md:hidden"></div>

        <h4 class="mt-3 break-words font-sans text-sm md:hidden">
          {{ section.title }}
        </h4>

        <HeightTransition>
          <div
            v-if="section.type === expandedSubSection"
            class="flex w-full flex-col items-end gap-1 lg:pr-14"
          >
            <span
              v-for="item in section.professionalItems"
              :key="item"
              class="w-full break-words text-right text-xs font-semibold text-gray-600 first:mt-4"
              >{{ item }}</span
            >
          </div>
        </HeightTransition>
        <HeightTransition>
          <div v-if="section.type === expandedSubSection"></div>
        </HeightTransition>
        <HeightTransition>
          <div
            v-if="section.type === expandedSubSection"
            class="flex w-full flex-col gap-1 lg:pl-14"
          >
            <span
              v-for="item in section.companyItems"
              :key="item"
              class="w-full break-words text-xs font-semibold text-gray-600 first:mt-4"
              >{{ item }}</span
            >
          </div>
        </HeightTransition>
      </section>
    </div>
  </div>
</template>
